/* The Locket Tree: complete, in-stock collection and search filtering. Draft theme only. */
(function (global) {
  'use strict';

  var PREVIEW_KEYS = ['preview_theme_id', '_fd', 'pb'];
  var SORT_LABELS = [
    ['manual', 'Featured'], ['best-selling', 'Best selling'], ['relevance', 'Relevance'],
    ['price-ascending', 'Price, low to high'], ['price-descending', 'Price, high to low']
  ];
  var collectionCache = new Map();
  var POPULATION_TTL = 5 * 60 * 1000;

  function themeContextUrl(currentUrl, themeId, themeRole) {
    var url = new URL(currentUrl);
    if (themeRole !== 'main' && /^\d+$/.test(String(themeId || ''))) {
      url.searchParams.set('preview_theme_id', String(themeId));
    }
    return url;
  }

  function collectionDataUrl(base, currentUrl, sort, page, defaultSort) {
    var current = new URL(currentUrl);
    var url = new URL(base, current);
    if (url.origin !== current.origin) throw new Error('Collection data must come from this shop.');
    url.search = '';
    url.hash = '';
    url.searchParams.set('section_id', 'tlt-collection-data');
    url.searchParams.set('page', String(page));
    // Shopify already supplies its collection default without a sort parameter.
    // Keeping that URL clean also lets crawlers fetch the default collection data.
    // An unknown default must retain the explicit sort, as must every other order.
    var selectedSort = sort || 'manual';
    if (!defaultSort || selectedSort !== defaultSort) url.searchParams.set('sort_by', selectedSort);
    PREVIEW_KEYS.forEach(function (key) {
      if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
    });
    return url;
  }

  function preservePreview(href, currentUrl) {
    var current = new URL(currentUrl);
    var url = new URL(href, current);
    if (url.origin === current.origin) {
      PREVIEW_KEYS.forEach(function (key) {
        if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
      });
    }
    return url.href;
  }

  function collectionCacheKey(options) {
    return JSON.stringify([
      '20261024-native-price-v1',
      collectionDataUrl(options.collectionUrl, options.currentUrl, options.sort, 1, options.defaultSort).href,
      options.sort || 'manual', options.defaultSort || null,
      options.currency || 'GBP', options.locale || ''
    ]);
  }

  function validateCollectionPayload(payload, url) {
    var expected = new URL(url).searchParams.get('preview_theme_id');
    if (expected && (!/^\d+$/.test(expected) || !payload || String(payload.themeId) !== expected)) {
      throw new Error('Collection returned a different theme. Please reload this draft preview.');
    }
    return payload;
  }

  function createCollectionFetcher(fetcher, parser, settings) {
    settings = settings || {};
    var verified = new Map();
    var pageContext = settings.pageContext;
    if (!pageContext && global.location && global.Shopify && global.Shopify.theme) {
      pageContext = { url: global.location.href, themeId: global.Shopify.theme.id };
    }
    var pageOrigin = null;
    var pageTheme = null;
    if (pageContext && /^\d+$/.test(String(pageContext.themeId || ''))) {
      try {
        var pageUrl = new URL(pageContext.url);
        if (pageUrl.protocol === 'https:' || pageUrl.protocol === 'http:') {
          pageOrigin = pageUrl.origin;
          pageTheme = String(pageContext.themeId);
          verified.set(pageOrigin, pageTheme);
        }
      } catch (_) {}
    }
    var now = settings.now || Date.now;
    var timeout = settings.timeoutMs == null ? 20000 : settings.timeoutMs;
    var stopped = function (signal) {
      if (signal && signal.aborted) throw new global.DOMException('Loading cancelled.', 'AbortError');
    };
    return async function readCollection(url, signal) {
      var original = new URL(url);
      if ((original.protocol !== 'https:' && original.protocol !== 'http:') || original.username || original.password ||
          (pageOrigin && original.origin !== pageOrigin)) throw new Error('Collection data must come from this shop.');
      var previewId = original.searchParams.get('preview_theme_id');
      if (previewId && !/^\d+$/.test(previewId)) throw new Error('Invalid collection preview identity.');
      var expectedTheme = previewId || (original.origin === pageOrigin ? pageTheme : null);
      var fast = !!previewId && verified.get(original.origin) === previewId;
      var deadline = now() + timeout;
      var request = async function (requestUrl) {
        stopped(signal);
        var remaining = deadline - now();
        if (remaining <= 0) throw new Error('Collection took too long to load. Please try again.');
        var html = await global.TLTSearch.fetchSearchText(requestUrl.href, signal, async function (input, options) {
          var response = await (fetcher || global.fetch)(input, options);
          if (response.url && new URL(response.url).origin !== original.origin) {
            throw new Error('Collection preview redirected outside this shop. Please reload the preview.');
          }
          return response;
        }, remaining);
        stopped(signal);
        var payload;
        if (parser) payload = parser(html);
        else {
          var parsed = new global.DOMParser().parseFromString(html, 'text/html');
          var data = parsed.querySelector('script[data-collection-data]');
          if (!data) throw new Error('Collection data is missing.');
          payload = JSON.parse(data.textContent);
        }
        validateCollectionPayload(payload, original);
        if (expectedTheme && (!payload || String(payload.themeId) !== expectedTheme)) {
          throw new Error('Collection returned a different theme. Please reload this draft preview.');
        }
        return payload;
      };
      var payload;
      try {
        var transport = new URL(original);
        if (fast) transport.searchParams.delete('preview_theme_id');
        payload = await request(transport);
      } catch (error) {
        if (!fast || (signal && signal.aborted) || error.name === 'AbortError' || now() >= deadline ||
            /outside this shop/.test(error.message)) throw error;
        // A different tab may replace the preview cookie. Reactivate this explicit
        // draft once; both requests retain one deadline and validate the same theme.
        verified.delete(original.origin);
        payload = await request(original);
      }
      if (previewId) verified.set(original.origin, previewId);
      return payload;
    };
  }

  var fetchCollectionData = createCollectionFetcher();

  function validateCollectionBootstrap(payload, options) {
    if (!payload || options.force) return null;
    try {
      var current = new URL(options.currentUrl);
      var documentUrl = new URL(options.documentUrl || options.currentUrl);
      var collection = new URL(options.collectionUrl, current);
      var path = function (url) { return url.pathname.replace(/\/$/, ''); };
      if (!['http:', 'https:'].includes(current.protocol) || collection.origin !== current.origin || documentUrl.origin !== current.origin ||
          path(current) !== path(collection) || path(documentUrl) !== path(collection) || payload.documentPath.replace(/\/$/, '') !== path(collection)) return null;
      var safeContext = function (url) {
        return Array.from(url.searchParams.keys()).every(function (key) {
          if (key === 'page') return url.searchParams.getAll(key).length === 1 && url.searchParams.get(key) === '1';
          if (key === 'sort_by' || key === 'preview_theme_id') return url.searchParams.getAll(key).length === 1;
          return ['_fd', 'pb', 'gclid', 'fbclid', 'msclkid', 'twclid'].includes(key) || key.indexOf('utm_') === 0 || key.indexOf('tlt.') === 0;
        });
      };
      if (!safeContext(current) || !safeContext(documentUrl)) return null;
      if (payload.schema !== 1 || payload.unfiltered !== true || payload.pageSize !== 50 ||
          !/^\d+$/.test(String(options.themeId || '')) || String(payload.themeId) !== String(options.themeId) ||
          !/^\d+$/.test(String(options.collectionId || '')) || String(payload.collectionId) !== String(options.collectionId) ||
          !options.collectionHandle || payload.collectionHandle !== options.collectionHandle ||
          new URL(payload.collectionUrl, current).href !== collection.href ||
          payload.sort !== options.sort || !options.currency || payload.currency !== options.currency ||
          !options.locale || payload.locale !== options.locale || !/^[A-Z]{2}$/.test(options.country || '') || payload.country !== options.country) return null;
      if (current.searchParams.has('preview_theme_id') && current.searchParams.get('preview_theme_id') !== String(payload.themeId)) return null;
      if (documentUrl.searchParams.has('preview_theme_id') && documentUrl.searchParams.get('preview_theme_id') !== String(payload.themeId)) return null;
      if (documentUrl.searchParams.has('sort_by') && documentUrl.searchParams.get('sort_by') !== payload.sort) return null;
      if (current.searchParams.has('sort_by') && current.searchParams.get('sort_by') !== payload.sort) return null;
      var renderedAt = Number(payload.renderedAt);
      if (!Number.isFinite(renderedAt) || Date.now() - renderedAt >= POPULATION_TTL || renderedAt - Date.now() > 60000) return null;
      var first = global.TLTSearch.validatePage(payload, 1);
      if (first.total !== payload.unfilteredTotal || first.pages !== Math.max(1, Math.ceil(first.total / 50)) ||
          first.items.length !== Math.min(50, first.total) || new Set(first.items.map(global.TLTSearch.itemKey)).size !== first.items.length) return null;
      return payload;
    } catch (_) { return null; }
  }

  async function loadCollection(options, signal, onProgress) {
    var key = collectionCacheKey(options);
    var cached = collectionCache.get(key);
    if (signal.aborted) throw new global.DOMException('Loading cancelled.', 'AbortError');
    if (options.force) collectionCache.delete(key);
    if (!options.force && cached && Date.now() - cached.time < POPULATION_TTL) return cached.items;
    var fetchPage = async function (page) {
      if (signal.aborted) throw new global.DOMException('Loading cancelled.', 'AbortError');
      var url = collectionDataUrl(options.collectionUrl, options.currentUrl, options.sort, page, options.defaultSort);
      var payload = await fetchCollectionData(url, signal);
      if ((payload.currency && payload.currency !== options.currency) ||
          (payload.country && options.country && payload.country !== options.country)) {
        var contextChanged = new Error('The collection market or currency changed. Please reload the page.');
        contextChanged.code = 'COLLECTION_CONTEXT_CHANGED';
        throw contextChanged;
      }
      var validated = global.TLTSearch.validatePage(payload, page);
      if (validated.items.length > validated.total || new Set(validated.items.map(global.TLTSearch.itemKey)).size !== validated.items.length) {
        throw new Error('Collection pagination is invalid.');
      }
      return payload;
    };
    var initial = validateCollectionBootstrap(options.initialPage, options);
    var first = initial || await fetchPage(1);
    if (signal.aborted) throw new global.DOMException('Loading cancelled.', 'AbortError');
    if (first.pages > 1 && typeof onProgress === 'function') onProgress({ items: first.items, complete: false });
    var items = await global.TLTSearch.loadNativePages(first, fetchPage, 3);
    if (signal.aborted) throw new global.DOMException('Loading cancelled.', 'AbortError');
    var fetchedAt = initial ? Math.min(Date.now(), Number(initial.renderedAt)) : Date.now();
    Object.defineProperty(items, 'fetchedAt', { value: fetchedAt, configurable: true });
    collectionCache.delete(key);
    collectionCache.set(key, { time: fetchedAt, items: items });
    while (collectionCache.size > 8) collectionCache.delete(collectionCache.keys().next().value);
    return items;
  }

  function amount(value) {
    if (value == null || String(value).trim() === '') return null;
    var parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? Number((parsed * 100).toFixed(8)) : null;
  }

  function displayedMinorUnits(formatted) {
    // The app's {{amount}} formatter always uses comma grouping and a decimal point.
    // It applies its configured .99/whole-number/three-decimal/BTC display rules first.
    var numeric = String(formatted).replace(/,/g, '').trim();
    if (!/^\d+(?:\.\d+)?$/.test(numeric)) throw new Error('The displayed currency amount could not be read.');
    var value = Number(numeric) * 100;
    if (!Number.isFinite(value) || value < 0) throw new Error('The displayed currency amount is invalid.');
    return Number(value.toFixed(8));
  }

  function baseCurrencyContext(baseCurrency, status) {
    return {
      base: baseCurrency, currency: baseCurrency, api: null,
      status: status, resolved: status === 'base', key: status + ':' + baseCurrency,
      precision: new Intl.NumberFormat('en', { style: 'currency', currency: baseCurrency }).resolvedOptions().maximumFractionDigits,
      convert: function (minor) { return minor == null ? minor : Number(minor); }
    };
  }

  function currencyContext(baseCurrency, host) {
    host = host || global;
    if (global.TLTSearch && typeof global.TLTSearch.currencyContext === 'function') {
      return global.TLTSearch.currencyContext(baseCurrency, host);
    }
    var api = host.DoublyGlobalCurrency;
    var expected = !!host.DoublyGlobal || !!(host.document && typeof host.document.querySelector === 'function' &&
      host.document.querySelector('script[src*="doubly.js"]'));
    if (!api) return baseCurrencyContext(baseCurrency, expected ? 'pending' : 'base');
    if (!api.currentCurrency) return baseCurrencyContext(baseCurrency, 'pending');
    if (!/^[A-Z]{3}$/.test(api.currentCurrency) || typeof api.convert !== 'function' ||
        typeof api.formatMoney !== 'function' || typeof api.convertAll !== 'function') {
      return baseCurrencyContext(baseCurrency, 'unavailable');
    }
    var currency = api.currentCurrency;
    var context = { base: baseCurrency, currency: currency, api: api, status: 'ready', resolved: true };
    context.format = function (minor) {
      if (currency === baseCurrency) {
        var nativePrecision = currency === 'BTC' ? 8 : new Intl.NumberFormat('en', {
          style: 'currency', currency: currency
        }).resolvedOptions().maximumFractionDigits;
        return (Number(minor) / 100).toFixed(nativePrecision);
      }
      var converted = currency === baseCurrency ? Number(minor) : api.convert(Number(minor), baseCurrency, currency);
      return api.formatMoney(converted, '{{amount}}', false, false);
    };
    context.convert = function (minor) {
      if (minor == null || !Number.isFinite(Number(minor))) return minor;
      return displayedMinorUnits(context.format(minor));
    };
    try {
      var formattedProbe = context.format(12345);
      displayedMinorUnits(formattedProbe);
      context.precision = (String(formattedProbe).split('.')[1] || '').length;
      // Detect both selection changes and newly available conversion/rounding data.
      var rateProbe = Number(api.convert(10000, baseCurrency, currency));
      if (!Number.isFinite(rateProbe) || rateProbe <= 0) throw new Error('Currency conversion is unavailable.');
      context.key = currency + ':app:' + rateProbe + ':' + formattedProbe;
    } catch (_) {
      return baseCurrencyContext(baseCurrency, 'unavailable');
    }
    return context;
  }

  function displayItems(items, context) {
    return items.map(function (item) {
      if (item.kind && item.kind !== 'product') return item;
      return Object.assign({}, item, {
        tltBasePrice: item.price,
        price: context.convert(item.price),
        variants: (Array.isArray(item.variants) ? item.variants : []).map(function (variant) {
          return Object.assign({}, variant, { tltBasePrice: variant.price, price: context.convert(variant.price) });
        })
      });
    });
  }

  function activeFilterCount(state) {
    var tags = Object.keys(state.selected || {}).reduce(function (total, key) {
      return total + state.selected[key].length;
    }, 0);
    return tags + (state.min != null || state.max != null ? 1 : 0);
  }

  function escapeText(value) {
    return String(value).replace(/[&<>"']/g, function (character) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character];
    });
  }

  function matchingPriceRecord(item, selection, options) {
    if (!selection) return item;
    var variant = selection.variant;
    var basePrice = variant.tltBasePrice == null ? variant.price : variant.tltBasePrice;
    var markup = typeof variant.priceHtml === 'string' && variant.priceHtml.trim() ? variant.priceHtml
      : '<span class="price__current money">' + escapeText(new Intl.NumberFormat(options.locale || 'en-GB', {
        style: 'currency', currency: options.currency || 'GBP'
      }).format(basePrice / 100)) + '</span>';
    var url = new URL(item.url, options.baseUrl);
    if (/^\d+$/.test(String(variant.id || ''))) url.searchParams.set('variant', String(variant.id));
    return Object.assign({}, item, {
      price: variant.price, tltBasePrice: basePrice,
      priceHtml: (selection.from ? 'From ' : '') + markup,
      url: url.href, matchedVariantId: variant.id || null
    });
  }

  function galleryImages(item, baseUrl) {
    var seen = new Set();
    var records = [[item.image, item.imageAlt || item.title || '']].concat(Array.isArray(item.gallery) ? item.gallery : []);
    return records.reduce(function (images, record) {
      if (!Array.isArray(record) || typeof record[0] !== 'string' || !record[0]) return images;
      try {
        var url = new URL(record[0], baseUrl);
        if (!/^https?:$/.test(url.protocol) || seen.has(url.href)) return images;
        seen.add(url.href);
        images.push({ src: url.href, alt: typeof record[1] === 'string' && record[1] ? record[1] : item.imageAlt || item.title || '' });
      } catch (_) {}
      return images;
    }, []);
  }

  function collectionImageSourceSet(source, baseUrl) {
    try {
      var url = new URL(source, baseUrl);
      var base = new URL(baseUrl);
      var shopImage = url.origin === base.origin && /^\/cdn\/shop\/(files|products)\//.test(url.pathname);
      var shopifyImage = url.hostname === 'cdn.shopify.com' && /^\/s\/files\//.test(url.pathname);
      // Only the existing Shopify width transformation is varied; retain the
      // image path, version, crop and every other query parameter unchanged.
      if (url.protocol !== 'https:' || url.username || url.password || (!shopImage && !shopifyImage)
          || url.searchParams.getAll('width').length !== 1 || !/^\d+$/.test(url.searchParams.get('width'))) return '';
      return [240, 360, 480, 540, 600, 750, 900, 1200, 1500, 1800].map(function (width) {
        var sized = new URL(url.href);
        sized.searchParams.set('width', String(width));
        return sized.href + ' ' + width + 'w';
      }).join(', ');
    } catch (_) { return ''; }
  }

  function collectionImageLayout(grid, mobileColumns, mobile) {
    var style = global.getComputedStyle && grid ? global.getComputedStyle(grid) : null;
    var tracks = style && /^(?:\d+(?:\.\d+)?px\s*)+$/.test(style.gridTemplateColumns || '')
      ? style.gridTemplateColumns.trim().split(/\s+/).map(parseFloat) : [];
    var configured = style && style.getPropertyValue ? Number(style.getPropertyValue('--tlt-desktop-columns')) : 0;
    var columns = tracks.length || (mobile ? (Number(mobileColumns) === 1 ? 1 : 2) : configured || 4);
    var width = tracks[0];
    if (!(width > 0) && grid && grid.clientWidth > 0) {
      var gap = style ? parseFloat(style.columnGap) : NaN;
      if (!Number.isFinite(gap)) gap = mobile ? 15 : 22;
      width = (grid.clientWidth - gap * (columns - 1)) / columns;
    }
    // The fallback is used only while the grid has no measurable layout. The
    // observer replaces it once visible, without changing the featured node.
    return { columns: columns, sizes: width > 0 ? (Math.ceil(width * 100) / 100) + 'px'
      : '(max-width: 767px) ' + (Number(mobileColumns) === 1 ? '100vw' : '50vw') + ', (max-width: 1100px) 33.34vw, ' + (100 / columns) + 'vw' };
  }

  function prepareCollectionImage(card, index, layout, baseUrl) {
    var image = card.querySelector('.image-cont img');
    if (!image) return;
    image.dataset.collectionFeatured = '';
    var sourceSet = collectionImageSourceSet(image.src, baseUrl);
    if (sourceSet) {
      // Set sizing before srcset, then priority/loading, while the card is still
      // detached. Do not change src: it remains the original 600px fallback.
      image.sizes = layout.sizes;
      image.srcset = sourceSet;
      image.dataset.collectionResponsive = '';
    }
    image.setAttribute('fetchpriority', index === 0 ? 'high' : 'auto');
    image.loading = index < layout.columns ? 'eager' : 'lazy';
  }

  function enhanceCollectionCard(card, item, baseUrl) {
    var images = galleryImages(item, baseUrl);
    var first = card.querySelector('.image-cont img');
    var container = card.querySelector('.image-cont');
    if (!Array.isArray(item.gallery) || images.length < 2 || !first || !container) return card;
    var doc = global.document;
    var gallery = doc.createElement('tlt-collection-gallery');
    var stack = doc.createElement('div');
    stack.className = 'tlt-gallery-stack';
    var link = first.parentNode;
    images.forEach(function (record, index) {
      var frame = doc.createElement('div');
      frame.className = 'product-block__image ' + (index ? 'product-block__image--secondary' : 'product-block__image--primary product-block__image--active');
      if (index === 1) frame.classList.add('product-block__image--show-on-hover');
      var img = index ? doc.createElement('img') : first;
      if (index) {
        // Deliberately no src/srcset: metadata must not start secondary downloads.
        img.dataset.gallerySrc = record.src;
        img.alt = record.alt;
        img.width = 600; img.height = 600; img.decoding = 'async';
      }
      frame.appendChild(img); stack.appendChild(frame);
    });
    link.replaceChildren(stack);
    container.classList.add('image-cont--with-secondary-image', 'image-cont--same-aspect-ratio');
    ['previous', 'next'].forEach(function (direction) {
      var button = doc.createElement('button');
      button.type = 'button'; button.disabled = true;
      button.className = 'image-page-button image-page-button--' + direction;
      button.dataset.galleryControl = '';
      button.setAttribute('aria-label', direction === 'previous' ? 'Previous image' : 'Next image');
      var svg = doc.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('width', '24'); svg.setAttribute('height', '24');
      svg.setAttribute('fill', 'none'); svg.setAttribute('stroke', 'currentColor'); svg.setAttribute('stroke-width', '1.3');
      svg.setAttribute('aria-hidden', 'true'); svg.setAttribute('focusable', 'false');
      var path = doc.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', direction === 'previous' ? 'm15 18-6-6 6-6' : 'm9 18 6-6-6-6');
      svg.appendChild(path); button.appendChild(svg); container.appendChild(button);
    });
    var dots = doc.createElement('div'); dots.className = 'product-block__image-dots';
    // Match the original compact indicators. The third dot means more photos;
    // accessible previous/next controls and swipe still reach every image.
    dots.setAttribute('aria-hidden', 'true');
    images.slice(0, 3).forEach(function (_, index) {
      var dot = doc.createElement('span');
      dot.className = 'product-block__image-dot' + (index === 0 ? ' product-block__image-dot--active' : '') +
        (index === 2 ? ' product-block__image-dot--more' : '');
      dots.appendChild(dot);
    });
    container.appendChild(dots);
    var status = doc.createElement('span'); status.className = 'visually-hidden'; status.dataset.galleryStatus = '';
    status.setAttribute('role', 'status'); status.setAttribute('aria-live', 'polite');
    container.appendChild(status);
    container.parentNode.replaceChild(gallery, container); gallery.appendChild(container);
    return card;
  }

  var helpers = { themeContextUrl: themeContextUrl, collectionDataUrl: collectionDataUrl, preservePreview: preservePreview,
    collectionCacheKey: collectionCacheKey, loadCollection: loadCollection, validateCollectionPayload: validateCollectionPayload,
    createCollectionFetcher: createCollectionFetcher, validateCollectionBootstrap: validateCollectionBootstrap,
    amount: amount, activeFilterCount: activeFilterCount, displayedMinorUnits: displayedMinorUnits,
    currencyContext: currencyContext, displayItems: displayItems, matchingPriceRecord: matchingPriceRecord,
    galleryImages: galleryImages, enhanceCollectionCard: enhanceCollectionCard,
    collectionImageSourceSet: collectionImageSourceSet, collectionImageLayout: collectionImageLayout,
    prepareCollectionImage: prepareCollectionImage };
  if (typeof module !== 'undefined' && module.exports) module.exports = helpers;
  if (!global.HTMLElement || !global.customElements || global.customElements.get('tlt-filtered-results')) return;

  class CollectionGallery extends global.HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      var Native = global.customElements.get('product-block');
      if (!Native) {
        if (!this.waiting) {
          this.waiting = true;
          global.customElements.whenDefined('product-block').then(() => { if (this.isConnected) this.connectedCallback(); });
        }
        return;
      }
      this.images = Array.from(this.querySelectorAll('.product-block__image'));
      if (this.images.length < 2) return;
      this.initialized = true; this.native = Native.prototype; this.activeIndex = 0;
      this.revision = 0; this.loads = new Map(); this.cancelLoads = new Set();
      this.imageContainer = this.querySelector('.image-cont--with-secondary-image');
      this.status = this.querySelector('[data-gallery-status]');
      // Reuse the theme's arrows/swipe, but not its constructor or timed preload.
      this.native.initImagePagination.call(this);
      this.addEventListener('mouseenter', () => { this.hovered = true; this.prepareHover(); });
      this.addEventListener('mouseleave', () => { this.hovered = false; this.classList.remove('tlt-gallery-hover-ready'); });
      this.addEventListener('keydown', (event) => {
        if (!event.target.closest('[data-gallery-control]')) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault(); this.incrementActiveImage(event.key === 'ArrowLeft' ? -1 : 1);
        }
      });
      this.imageContainer.addEventListener('touchstart', (event) => {
        this.touchOrigin = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      }, { passive: true });
      this.imageContainer.addEventListener('touchmove', (event) => {
        if (this.touchOrigin && Math.abs(event.touches[0].clientY - this.touchOrigin.y) < 30 &&
            Math.abs(event.touches[0].clientX - this.touchOrigin.x) > 25) this.suppressLinkUntil = Date.now() + 500;
      }, { passive: true });
      this.imageContainer.addEventListener('touchend', () => { this.touchOrigin = null; }, { passive: true });
      this.addEventListener('click', (event) => {
        if (Date.now() < (this.suppressLinkUntil || 0) && event.target.closest('a.product-link')) {
          event.preventDefault(); event.stopPropagation();
        }
      }, true);
      this.querySelectorAll('[data-gallery-control]').forEach(function (button) { button.disabled = false; });
    }

    disconnectedCallback() {
      this.revision = (this.revision || 0) + 1;
      this.hovered = false;
      this.pendingIndex = null;
      this.classList.remove('tlt-gallery-hover-ready');
      if (this.touchOrigin && global.theme) global.theme.productBlockTouchTracking = false;
      this.touchOrigin = null;
      if (this.cancelLoads) Array.from(this.cancelLoads).forEach(function (cancel) { cancel(); });
      if (this.loads) this.loads.clear();
    }

    getImageAt(index) { return this.native.getImageAt.call(this, index); }

    loadImage(index) {
      var frame = this.getImageAt(index); var img = frame.querySelector('img');
      if (!img.dataset.gallerySrc || img.dataset.galleryReady === 'true') return Promise.resolve(true);
      if (this.loads.has(img)) return this.loads.get(img);
      var finish;
      var request = new Promise((resolve) => {
        var onLoad = () => finish(true); var onError = () => finish(false);
        finish = (ready) => {
          img.removeEventListener('load', onLoad); img.removeEventListener('error', onError);
          this.cancelLoads.delete(cancel);
          if (ready) img.dataset.galleryReady = 'true';
          resolve(ready);
        };
        var cancel = () => finish(false);
        this.cancelLoads.add(cancel);
        img.addEventListener('load', onLoad); img.addEventListener('error', onError);
        img.loading = 'eager'; img.src = img.dataset.gallerySrc;
        if (img.complete && img.naturalWidth > 0) finish(true);
      });
      this.loads.set(img, request);
      request.then(() => { if (this.loads.get(img) === request) this.loads.delete(img); });
      return request;
    }

    prepareHover() {
      if (!this.hovered || !this.isConnected) return;
      var revision = this.revision; var index = this.activeIndex;
      this.loadImage(index + 1).then((ready) => {
        if (this.isConnected && this.hovered && this.revision === revision && this.activeIndex === index) {
          this.classList.toggle('tlt-gallery-hover-ready', ready);
        }
      });
    }

    incrementActiveImage(increment) {
      this.setActiveImage((this.pendingIndex == null ? this.activeIndex : this.pendingIndex) + increment);
    }

    setActiveImage(index) {
      index = ((index % this.images.length) + this.images.length) % this.images.length;
      var revision = ++this.revision; this.pendingIndex = index;
      this.classList.remove('tlt-gallery-hover-ready');
      this.loadImage(index).then((ready) => {
        if (!this.isConnected || this.revision !== revision) return;
        this.pendingIndex = null;
        if (!ready) { this.status.textContent = 'This image could not load. Please try again.'; return; }
        this.native.setActiveImage.call(this, index);
        this.activeIndex = index;
        this.querySelectorAll('.product-block__image-dot').forEach(function (dot, position) {
          dot.classList.toggle('product-block__image-dot--active', position === Math.min(index, 2));
        });
        this.status.textContent = 'Image ' + (index + 1) + ' of ' + this.images.length;
      });
    }
  }
  global.customElements.define('tlt-collection-gallery', CollectionGallery);

  class FilteredResults extends global.HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.engine = global.TLTFilters;
      this.search = global.TLTSearch;
      this.results = this.querySelector('[data-filter-results]');
      this.count = this.querySelector('[data-filter-count]');
      this.status = this.querySelector('[data-filter-status]');
      this.facets = this.querySelector('[data-filter-facets]');
      this.chips = this.querySelector('[data-filter-chips]');
      this.sidebar = this.querySelector('[data-filter-sidebar]');
      this.sort = this.querySelector('[data-filter-sort]');
      this.loadMoreButton = this.querySelector('[data-filter-more]');
      this.shown = this.querySelector('[data-filter-shown]');
      this.form = this.querySelector('[data-filter-search-form]');
      this.filterButton = this.querySelector('[data-filter-open]');
      this.backdrop = this.querySelector('[data-filter-backdrop]');
      this.sentinel = this.querySelector('[data-filter-sentinel]');
      if (!this.engine || !this.search || !this.results || !this.sidebar) {
        if (this.status) this.status.textContent = 'The filters could not load. Please refresh the page.';
        if (this.results) this.results.setAttribute('aria-busy', 'false');
        return;
      }
      this.mode = this.dataset.mode === 'collection' ? 'collection' : 'search';
      this.initialCollectionData = null;
      this.initialDocumentUrl = global.location.href;
      if (this.mode === 'collection') {
        var initialData = this.querySelector('script[data-collection-bootstrap]');
        if (initialData) {
          try { this.initialCollectionData = JSON.parse(initialData.textContent); } catch (_) {}
          initialData.remove();
        }
      }
      this.pageSize = Math.min(50, Math.max(1, Number(this.dataset.pageSize) || 50));
      this.defaultSort = this.dataset.defaultSort || (this.mode === 'search' ? 'relevance' : 'manual');
      this.tree = this.engine.chooseTree(this.mode, this.dataset.collectionTitle || '');
      this.dataset.filterTree = this.tree.key;
      this.media = global.matchMedia('(max-width: 767px)');
      this.collapse = new Map();
      this.state = this.readPageState();
      this.onClick = (event) => this.handleClick(event);
      this.onChange = (event) => this.handleChange(event);
      this.onInput = (event) => this.handleInput(event);
      this.onSubmit = (event) => {
        if (!this.form || event.target !== this.form) return;
        event.preventDefault();
        this.state = Object.assign({}, this.state, {
          q: this.form.querySelector('[name="q"]').value, page: 1, min: null, max: null, selected: {}
        });
        this.navigate();
      };
      this.onPopState = () => {
        this.revealResultsOnRender = false;
        this.priceRangeReset = false;
        this.state = this.readPageState();
        this.refresh();
      };
      this.onMedia = () => {
        this.closeDrawer(false);
        this.syncCollectionLayout();
        this.syncDrawer();
        if (this.items && !this.loading) this.renderFacets();
      };
      this.onReturn = () => this.refreshIfStale();
      this.onKey = (event) => this.handleKey(event);
      this.onFocus = (event) => {
        if (this.drawerOpen && !this.sidebar.contains(event.target)) this.sidebar.focus();
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.addEventListener('submit', this.onSubmit);
      global.addEventListener('popstate', this.onPopState);
      global.addEventListener('focus', this.onReturn);
      global.document.addEventListener('visibilitychange', this.onReturn);
      global.document.addEventListener('keydown', this.onKey);
      global.document.addEventListener('focusin', this.onFocus);
      this.media.addEventListener('change', this.onMedia);
      this.renderSortOptions();
      this.syncCollectionLayout();
      this.syncDrawer();
      this.startCollectionImageWatch();
      this.startCurrencyWatch();
      if (global.IntersectionObserver) {
        this.observer = new global.IntersectionObserver((entries) => {
          if (entries.some(function (entry) { return entry.isIntersecting; }) && !this.loading && !this.drawerOpen) {
            this.showMore(false);
          }
        }, { rootMargin: '250px' });
        this.observer.observe(this.sentinel);
      }
      this.refresh();
    }

    disconnectedCallback() {
      this.revealResultsOnRender = false;
      if (this.controller) this.controller.abort();
      if (this.observer) this.observer.disconnect();
      if (this.currencyObserver) this.currencyObserver.disconnect();
      if (this.currencyTimer) global.clearInterval(this.currencyTimer);
      this.stopCollectionImageWatch();
      this.closeDrawer(false);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('submit', this.onSubmit);
      global.removeEventListener('popstate', this.onPopState);
      global.removeEventListener('focus', this.onReturn);
      global.document.removeEventListener('visibilitychange', this.onReturn);
      global.document.removeEventListener('keydown', this.onKey);
      global.document.removeEventListener('focusin', this.onFocus);
      if (this.media) this.media.removeEventListener('change', this.onMedia);
      this.started = false;
    }

    el(tag, className, text) {
      var element = global.document.createElement(tag);
      if (className) element.className = className;
      if (text != null) element.textContent = text;
      return element;
    }

    money(minor, code) {
      code = code || this.displayCurrency();
      var precision = this.currency && code === this.currency.currency ? this.currency.precision
        : code === 'BTC' ? 8 : new Intl.NumberFormat('en', { style: 'currency', currency: code }).resolvedOptions().maximumFractionDigits;
      return new Intl.NumberFormat(this.dataset.locale || 'en-GB', {
        style: 'currency', currency: code, minimumFractionDigits: precision, maximumFractionDigits: precision
      }).format(minor / 100);
    }

    displayCurrency() {
      return this.currency ? this.currency.currency : this.dataset.currency || 'GBP';
    }

    contextUrl() {
      return themeContextUrl(global.location.href, this.dataset.themeId, this.dataset.themeRole).href;
    }

    readPageState() {
      var state = this.engine.readState(global.location.href, this.tree, this.defaultSort);
      var params = new URL(global.location.href).searchParams;
      var currency = params.get('tlt.currency');
      state.legacyFilters = Array.from(params.keys()).some(function (key) { return key.indexOf('pf_') === 0; });
      state.currency = /^[A-Z]{3}$/.test(currency || '') ? currency : this.dataset.currency || 'GBP';
      if ((this.mode === 'collection' && state.sort === 'relevance') ||
          (this.mode === 'search' && ['manual', 'best-selling'].indexOf(state.sort) >= 0)) {
        state.sort = this.defaultSort;
      }
      return state;
    }

    stateUrl() {
      var url = this.engine.stateUrl(this.contextUrl(), this.state, this.tree);
      if (this.mode === 'collection') url.searchParams.delete('filter.v.availability');
      if (this.state.min != null || this.state.max != null) url.searchParams.set('tlt.currency', this.state.currency || this.displayCurrency());
      return url;
    }

    currentCurrencyContext() {
      var base = this.dataset.currency || 'GBP';
      var next = currencyContext(base);
      if (!next.api && next.status === 'base' && (this.state.min != null || this.state.max != null) &&
          this.state.currency && this.state.currency !== base) next = baseCurrencyContext(base, 'pending');
      if (next.status === 'pending') {
        if (!this.pendingCurrencySince) this.pendingCurrencySince = Date.now();
        if (Date.now() - this.pendingCurrencySince >= 20000) next = baseCurrencyContext(base, 'unavailable');
      } else this.pendingCurrencySince = null;
      return next;
    }

    startCurrencyWatch() {
      // Currency selection is also changed by the app's jQuery handlers, which do not
      // dispatch a native change event. Observe its public price attributes and use a
      // cheap fallback check for an empty result set with no prices in the document.
      var reconcile = () => {
        var next;
        try { next = this.currentCurrencyContext(); }
        catch (_) { return; }
        if (!this.items || this.loading) {
          // Server prices remain useful even while the full filtering population
          // is unavailable. Their immutable native amounts must follow the same
          // currency selection as subsequently enhanced cards.
          if (this.search && typeof this.search.formatManagedPrices === 'function' && this.results) {
            try {
              this.search.formatManagedPrices(this.results, next, {
                sourceCurrency: this.dataset.currency || 'GBP', locale: this.dataset.locale || 'en-GB', host: global
              });
            } catch (_) { return; }
          }
          this.currency = next;
          if (this.collectionLoadFailed && this.status && this.status.children[0]) {
            this.status.children[0].textContent = [this.currencyNotice(),
              this.collectionContextChanged ? (this.dataset.collectionContextChanged || 'Your market or currency changed. Reload this page for current prices.') :
                this.collectionLoadingNotice(true, this.captureCollectionView())].filter(Boolean).join(' ');
          }
          return;
        }
        if (this.currencyFailure && this.currencyFailure.source === this.items &&
            this.currencyFailure.key === next.key && Date.now() < this.currencyFailure.retryAt) return;
        if (!this.currency || next.key !== this.currency.key) this.render();
      };
      if (global.MutationObserver) {
        this.currencyObserver = new global.MutationObserver(reconcile);
        this.currencyObserver.observe(global.document.body, { subtree: true, attributes: true, attributeFilter: ['doubly-currency'] });
      }
      this.currencyTimer = global.setInterval(reconcile, 1000);
      reconcile();
    }

    prepareDisplayItems() {
      var base = this.dataset.currency || 'GBP';
      var next = this.currentCurrencyContext();
      var boundsCurrency = this.state.currency || base;
      var hasPrice = this.state.min != null || this.state.max != null;
      // A saved foreign price range is not evidence that the customer changed to GBP.
      // Keep it pending even if the app's configuration script has not appeared yet.
      if (this.currencyFailure && this.currencyFailure.source === this.items &&
          this.currencyFailure.key === next.key && Date.now() < this.currencyFailure.retryAt) {
        next = baseCurrencyContext(base, 'unavailable');
      }
      if (this.pricedSource !== this.items || this.pricedCurrencyKey !== next.key) {
        try {
          this.pricedItems = displayItems(this.items, next);
          if (next.resolved) this.currencyFailure = null;
        } catch (_) {
          this.currencyFailure = { key: next.key, source: this.items, retryAt: Date.now() + 30000 };
          next = baseCurrencyContext(base, 'unavailable');
          this.pricedItems = displayItems(this.items, next);
        }
        this.pricedSource = this.items;
        this.pricedCurrencyKey = next.key;
      }
      var resetPrice = next.resolved && boundsCurrency !== next.currency && hasPrice;
      if (resetPrice) {
        this.state.min = null;
        this.state.max = null;
        this.state.page = 1;
        this.priceRangeReset = true;
      }
      this.currency = next;
      this.pricePaused = !next.resolved && hasPrice && boundsCurrency !== next.currency;
      if (next.resolved || !hasPrice) this.state.currency = next.currency;
      this.priceState = this.pricePaused ? Object.assign({}, this.state, { min: null, max: null }) : this.state;
      if (resetPrice) global.history.replaceState(null, '', this.stateUrl());
    }

    currencyNotice() {
      var reset = this.priceRangeReset ? 'Price range reset because the currency changed.' : '';
      if (!this.currency || this.currency.resolved) {
        return reset ? reset + ' Choose a new price range in ' + this.displayCurrency() + '.' : '';
      }
      var notice = this.currency.status === 'pending'
        ? ''
        : 'Prices are shown in ' + this.displayCurrency() + ' because currency conversion is currently unavailable.';
      if (this.pricePaused) notice += (notice ? ' ' : '') + 'Your ' + this.state.currency + ' price range is saved and paused until conversion is ready.';
      return [reset, notice].filter(Boolean).join(' ');
    }

    legacyNotice() {
      return this.state.legacyFilters ? 'This saved link uses the previous filter format. Select your filters again to apply them.' : '';
    }

    renderSortOptions() {
      this.sort.replaceChildren();
      var choices = SORT_LABELS.filter((entry) => this.mode === 'search'
        ? ['manual', 'best-selling'].indexOf(entry[0]) === -1 : entry[0] !== 'relevance');
      // Preserve collection defaults and saved URLs without offering the removed sorts.
      if (!choices.some((entry) => entry[0] === this.state.sort)) {
        var current = this.el('option', '', this.state.sort === this.defaultSort ? 'Default order' : 'Current order');
        current.value = this.state.sort;
        current.disabled = true;
        current.hidden = true;
        this.sort.appendChild(current);
      }
      choices.forEach((entry) => {
          var option = this.el('option', '', entry[1]);
          option.value = entry[0];
          this.sort.appendChild(option);
        });
      this.sort.value = this.state.sort;
    }

    handleClick(event) {
      var moreLink = event.target.closest('a[data-filter-more]');
      if (moreLink && this.contains(moreLink)) {
        // Keep modifier clicks, new tabs and copied links as ordinary navigation.
        if (event.defaultPrevented || (event.button != null && event.button !== 0) ||
            event.ctrlKey || event.metaKey || event.shiftKey || event.altKey ||
            moreLink.hasAttribute('download') ||
            (moreLink.getAttribute('target') && moreLink.getAttribute('target') !== '_self')) return;
        if (this.loading) return;
        event.preventDefault();
        this.showMore(true);
        return;
      }
      var button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.hasAttribute('data-filter-open')) this.openDrawer();
      else if (button.hasAttribute('data-filter-columns') && this.mode === 'collection') this.setMobileColumns(button.dataset.filterColumns);
      else if (button.hasAttribute('data-filter-close') || button.hasAttribute('data-filter-backdrop')) this.closeDrawer(true);
      else if (button.hasAttribute('data-filter-more')) this.showMore(true);
      else if (button.hasAttribute('data-filter-retry')) this.refresh(true);
      else if (button.hasAttribute('data-filter-clear')) {
        this.state.selected = {};
        this.state.min = null;
        this.state.max = null;
        this.state.page = 1;
        this.navigate();
      } else if (button.hasAttribute('data-remove-value')) {
        var key = button.dataset.removeKey;
        if (key === 'price') { this.state.min = null; this.state.max = null; }
        else {
          this.state.selected[key] = (this.state.selected[key] || []).filter(function (value) {
            return value !== button.dataset.removeValue;
          });
        }
        this.state.page = 1;
        this.navigate();
      }
    }

    handleChange(event) {
      var input = event.target;
      if (input === this.sort) {
        this.state.sort = input.value;
        this.state.page = 1;
        this.navigate();
        return;
      }
      if (input.hasAttribute('data-facet-key')) {
        var key = input.dataset.facetKey;
        var facet = this.tree.options.find(function (entry) { return entry.key === key; });
        var values = (this.state.selected[key] || []).filter(function (value) { return value !== input.value; });
        if (input.checked) {
          if (facet.selection === 'single') values = [];
          values.push(input.value);
        }
        this.state.selected[key] = values;
        this.state.page = 1;
        this.navigate();
        return;
      }
      if (input.hasAttribute('data-price-bound')) {
        if (this.currency && !this.currency.resolved) return;
        var bound = input.dataset.priceBound;
        this.state[bound] = amount(input.value);
        this.state.currency = this.displayCurrency();
        if (this.state.min != null && this.state.max != null && this.state.min > this.state.max) {
          this.state[bound === 'min' ? 'max' : 'min'] = this.state[bound];
        }
        this.state.page = 1;
        this.navigate();
      }
    }

    handleInput(event) {
      var input = event.target;
      if (input.type !== 'range' || !input.hasAttribute('data-price-bound')) return;
      var number = this.facets.querySelector('input[type="number"][data-price-bound="' + input.dataset.priceBound + '"]');
      if (number) number.value = input.value;
      input.setAttribute('aria-valuetext', this.money(Number(input.value) * 100));
      var sliders = input.closest('.tlt-filter-price-sliders');
      var minInput = sliders.querySelector('[data-price-bound="min"]');
      var maxInput = sliders.querySelector('[data-price-bound="max"]');
      var span = Number(input.max) - Number(input.min);
      var from = Math.max(0, Math.min(100, (Number(minInput.value) - Number(input.min)) / span * 100));
      var to = Math.max(from, Math.min(100, (Number(maxInput.value) - Number(input.min)) / span * 100));
      var active = sliders.querySelector('.tlt-filter-price-track-active');
      active.style.left = from + '%';
      active.style.right = (100 - to) + '%';
    }

    navigate() {
      // Keep the reset explanation through background refreshes and converter
      // settling; dismiss it only when the customer next changes the filters/query.
      this.priceRangeReset = false;
      this.state.legacyFilters = false;
      this.revealResultsOnRender = true;
      global.history.pushState(null, '', this.stateUrl());
      this.refresh();
    }

    populationKey() {
      return this.mode === 'search' ? 'search:' + this.state.q
        : 'collection:' + this.dataset.collectionUrl + ':' + this.state.sort;
    }

    snapshotFresh() {
      return Number.isFinite(this.loadedAt) && Date.now() - this.loadedAt < POPULATION_TTL;
    }

    refreshIfStale() {
      if (!global.document.hidden && this.items && !this.loading && !this.snapshotFresh()) return this.refresh(true);
    }

    captureCollectionView() {
      if (this.mode !== 'collection') return null;
      var pagination = this.querySelector('[data-filter-server-pagination]');
      var view = {
        nodes: Array.from(this.results.childNodes || this.results.children),
        hasProducts: this.results.children.length > 0 && !this.results.querySelector('.tlt-filter-server-empty'),
        serverRendered: this.results.dataset.filterServerRendered === 'true',
        page: this.results.dataset.serverPage,
        pagination: pagination,
        paginationHidden: pagination ? pagination.hidden : true
      };
      if (view.serverRendered && !this.serverCollectionView) this.serverCollectionView = view;
      return view;
    }

    restoreCollectionView(view) {
      if (!view) return;
      this.results.replaceChildren(...view.nodes);
      this.results.dataset.filterServerRendered = view.serverRendered ? 'true' : 'false';
      if (view.pagination) view.pagination.hidden = view.paginationHidden;
    }

    collectionLoadingNotice(failed, view) {
      var pendingSelection = activeFilterCount(this.state) > 0 || this.state.legacyFilters ||
        this.state.sort !== this.defaultSort || (view && !view.serverRendered);
      var notice = failed ? (this.dataset.collectionUnavailable || 'Filters and sorting are unavailable.') :
        (this.dataset.collectionLoading || 'Loading filters and sorting…');
      if (view && view.hasProducts) {
        notice += ' ' + (pendingSelection ? (this.dataset.collectionSelectionPending || 'The products below may not match your current selection.') :
          (this.dataset.collectionBrowse || 'You can browse the products below.'));
      }
      return notice + (failed ? ' ' + (this.dataset.collectionRetry || 'Please try again.') : '');
    }

    async refresh(force) {
      var key = this.populationKey();
      if (this.form) this.form.querySelector('[name="q"]').value = this.state.q;
      this.renderSortOptions();
      if (!force && !this.loading && this.items && this.loadedKey === key && this.snapshotFresh()) {
        this.render();
        return;
      }
      force = !!force || (this.items && !this.snapshotFresh());
      if (this.controller) this.controller.abort();
      var controller = new global.AbortController();
      this.controller = controller;
      var retainedView = this.captureCollectionView();
      this.collectionLoadFailed = false;
      this.collectionContextChanged = false;
      this.loading = true;
      this.results.setAttribute('aria-busy', 'true');
      this.items = null;
      this.loadedKey = null;
      this.matches = [];
      if (this.mode !== 'collection') this.results.replaceChildren();
      this.facets.replaceChildren();
      this.chips.replaceChildren();
      this.chips.hidden = true;
      this.count.textContent = '';
      this.shown.textContent = '';
      this.sort.disabled = true;
      this.filterButton.disabled = true;
      this.loadMoreButton.hidden = true;
      this.sentinel.hidden = true;
      this.status.textContent = [this.legacyNotice(), this.currencyNotice(), this.mode === 'collection' ? this.collectionLoadingNotice(false, retainedView) : 'Loading results…'].filter(Boolean).join(' ');
      this.setAttribute('data-ready', 'false');
      if (this.mode === 'search' && !this.state.q.trim()) {
        this.revealResultsOnRender = false;
        this.status.textContent = [this.currencyNotice(), 'Enter a search term.'].filter(Boolean).join(' ');
        this.loading = false;
        this.results.setAttribute('aria-busy', 'false');
        return;
      }
      try {
        var options = {
          q: this.state.q, sort: this.state.sort, defaultSort: this.dataset.defaultSort, currentUrl: this.contextUrl(),
          searchUrl: this.dataset.searchUrl || '/search',
          predictiveUrl: this.dataset.predictiveUrl || '/search/suggest',
          collectionUrl: this.dataset.collectionUrl || global.location.pathname,
          currency: this.dataset.currency || 'GBP', locale: this.dataset.locale || 'en-GB',
          themeId: this.dataset.themeId, force: !!force,
          collectionId: this.dataset.collectionId, collectionHandle: this.dataset.collectionHandle,
          country: global.Shopify && global.Shopify.country || this.dataset.country,
          documentUrl: this.initialDocumentUrl,
          initialPage: this.initialCollectionData
        };
        // Rendered records are a one-use initial-page hint, never a refresh cache.
        this.initialCollectionData = null;
        if (force && this.mode === 'collection') collectionCache.delete(collectionCacheKey(options));
        var onProgress = (progress) => {
            if (controller.signal.aborted || this.controller !== controller || !progress || !Array.isArray(progress.items)) return;
            this.renderPartial(progress.items);
          };
        var items = this.mode === 'collection'
          ? await loadCollection(options, controller.signal, onProgress)
          : await this.search.getItems(options, controller.signal, onProgress);
        if (controller.signal.aborted || this.controller !== controller) return;
        this.items = items;
        this.loadedKey = key;
        this.loadedAt = Number(items.fetchedAt) || Date.now();
        this.loading = false;
        this.render();
        this.sort.disabled = false;
        this.filterButton.disabled = false;
        this.setAttribute('data-ready', 'true');
      } catch (error) {
        if (controller.signal.aborted || this.controller !== controller || error.name === 'AbortError') return;
        this.revealResultsOnRender = false;
        controller.abort();
        this.items = null;
        this.matches = [];
        if (this.mode === 'collection') {
          // A failed refresh must not remove usable links, including if rendering
          // failed after constructing the replacement grid. Never label old facets
          // or counts as belonging to the pending selection.
          this.restoreCollectionView(retainedView || this.serverCollectionView);
          this.collectionLoadFailed = true;
          this.loadedKey = null;
          this.loadedAt = null;
          this.facets.replaceChildren();
          this.chips.replaceChildren();
          this.chips.hidden = true;
          this.count.textContent = '';
          this.shown.textContent = '';
          this.sort.disabled = true;
          this.filterButton.disabled = true;
          this.loadMoreButton.hidden = true;
          this.sentinel.hidden = true;
          this.setAttribute('data-ready', 'false');
        } else this.results.replaceChildren();
        var refine = this.mode === 'search' && error.code === 'SEARCH_QUERY_TOO_BROAD';
        this.collectionContextChanged = this.mode === 'collection' && error.code === 'COLLECTION_CONTEXT_CHANGED';
        var errorNotice = refine ? 'Please use a more specific search to see complete results. ' :
          (this.collectionContextChanged ? (this.dataset.collectionContextChanged || 'Your market or currency changed. Reload this page for current prices.') :
          this.mode === 'collection' ? this.collectionLoadingNotice(true, retainedView) :
            'We couldn’t load all the results. Please try again. ');
        this.status.replaceChildren(this.el('span', '', [this.currencyNotice(), errorNotice].filter(Boolean).join(' ')));
        var retry = this.el('button', 'btn btn--secondary tlt-filter-retry', this.collectionContextChanged ?
          (this.dataset.collectionReload || 'Reload page') : refine ? 'Refine search' : 'Try again');
        retry.type = 'button';
        if (this.collectionContextChanged) retry.addEventListener('click', () => global.location.reload());
        else if (refine) retry.addEventListener('click', () => {
          var input = this.form && this.form.querySelector('[name="q"]');
          if (input) { input.focus(); if (typeof input.select === 'function') input.select(); }
        });
        else retry.dataset.filterRetry = '';
        this.status.appendChild(retry);
      } finally {
        if (this.controller === controller) {
          this.loading = false;
          this.results.setAttribute('aria-busy', 'false');
        }
      }
    }

    renderPartial(items) {
      // The server-rendered collection (or the last complete view) remains usable
      // until every source page is validated. A partial eight-card preview must
      // never overwrite it. Search retains its existing progressive previews.
      if (this.mode === 'collection') return;
      // Early cards are a preview only. Counts, facets and pagination remain
      // unavailable until every source page has been validated and merged.
      this.items = items;
      this.prepareDisplayItems();
      this.matches = this.engine.sortItems(this.engine.filterItems(this.pricedItems, this.priceState || this.state, this.tree), this.state.sort, this.priceState || this.state);
      this.renderCards(0, 8, true);
      this.status.textContent = [this.legacyNotice(), this.currencyNotice(), this.mode === 'collection' ? 'Loading remaining products…' : 'Loading remaining results…'].filter(Boolean).join(' ');
    }

    render() {
      var focus = global.document.activeElement;
      var focusKey = focus && this.contains(focus) && focus.dataset.controlKey;
      this.prepareDisplayItems();
      this.matches = this.engine.sortItems(this.engine.filterItems(this.pricedItems, this.priceState || this.state, this.tree), this.state.sort, this.priceState || this.state);
      var pages = Math.max(1, Math.ceil(this.matches.length / this.pageSize));
      var previousPage = this.state.page;
      this.state.page = Math.max(1, Math.min(this.state.page || 1, pages));
      if (this.state.page !== previousPage) {
        global.history.replaceState(null, '', this.stateUrl());
      }
      this.count.textContent = this.matches.length + (this.mode === 'collection'
        ? (this.matches.length === 1 ? ' product' : ' products')
        : (this.matches.length === 1 ? ' result' : ' results'));
      var emptyNotice = this.matches.length ? '' : 'No ' +
        (this.mode === 'collection' ? 'products' : 'results') + ' match your filters.';
      this.status.textContent = [this.legacyNotice(), this.currencyNotice(), emptyNotice].filter(Boolean).join(' ');
      if (this.mode === 'search') global.document.title = this.search.resultTitle(
        this.matches.length, this.state.q, this.dataset.shopName);
      this.renderFacets();
      this.renderChips();
      this.renderCards(0, this.state.page * this.pageSize, true);
      this.renderProgress();
      if (focusKey) {
        var restored = Array.from(this.querySelectorAll('[data-control-key]')).find(function (element) {
          return element.dataset.controlKey === focusKey;
        });
        if (restored) restored.focus({ preventScroll: true });
        else if (this.drawerOpen) this.sidebar.querySelector('[data-filter-close]').focus();
      }
      this.revealUpdatedResults();
      if (this.mode === 'collection') {
        var pagination = this.querySelector('[data-filter-server-pagination]');
        if (pagination) pagination.hidden = true;
        this.results.dataset.filterServerRendered = 'false';
      }
    }

    revealUpdatedResults() {
      // A filter can replace a long grid with one row while leaving the viewport
      // beside the footer. Only explicit navigation should bring results back:
      // currency updates, load-more and browser history keep their own position.
      if (!this.revealResultsOnRender || this.loading || this.drawerOpen) return;
      this.revealResultsOnRender = false;
      var target = this.querySelector('.tlt-filter-toolbar') || this.results;
      if (!target) return;
      var sticky = global.theme && typeof global.theme.stickyHeaderHeight === 'function'
        ? global.theme.stickyHeaderHeight()
        : parseFloat(global.getComputedStyle(global.document.documentElement).getPropertyValue('--theme-sticky-header-height')) || 0;
      var top = target.getBoundingClientRect().top;
      var inset = sticky + 12;
      if (top < inset) {
        global.scrollTo({ top: Math.max(0, global.scrollY + top - inset), behavior: 'instant' });
      }
    }

    renderCards(start, end, replace) {
      var fragment = global.document.createDocumentFragment();
      var priceFormattingUnavailable = false;
      var context = this.contextUrl();
      var imageLayout = this.mode === 'collection'
        ? collectionImageLayout(this.results, this.mobileColumns, this.media && this.media.matches) : null;
      this.matches.slice(start, end).forEach((item, offset) => {
        item = matchingPriceRecord(item, this.engine.priceSelection(item, this.priceState || this.state), {
          currency: this.dataset.currency || 'GBP', locale: this.dataset.locale || 'en-GB', baseUrl: context
        });
        var hasPriceMarkup = typeof item.priceHtml === 'string' && item.priceHtml.trim();
        // createCard's numeric fallback also creates a .money span. Supply base
        // currency there so the app converts it once, just like Liquid price markup.
        var cardItem = hasPriceMarkup ? item : Object.assign({}, item, { price: item.tltBasePrice == null ? item.price : item.tltBasePrice });
        var card = this.search.createCard(cardItem, {
          currency: hasPriceMarkup ? this.displayCurrency() : this.dataset.currency || 'GBP', locale: this.dataset.locale || 'en-GB',
          sourceCurrency: this.dataset.currency || 'GBP',
          baseUrl: context
        });
        if (imageLayout) prepareCollectionImage(card, start + offset, imageLayout, context);
        if (this.mode === 'collection') card = enhanceCollectionCard(card, item, context);
        card.querySelectorAll('a[href]').forEach(function (link) {
          link.href = preservePreview(link.href, context);
        });
        if (typeof this.search.formatManagedPrices === 'function') {
          try {
            var formatted = this.search.formatManagedPrices(card, this.currency || this.currentCurrencyContext(), {
              sourceCurrency: this.dataset.currency || 'GBP', locale: this.dataset.locale || 'en-GB',
              currentAmount: item.tltBasePrice == null ? item.price : item.tltBasePrice, host: global
            });
            if (formatted && formatted.unavailable && this.currency && this.currency.resolved) priceFormattingUnavailable = true;
          } catch (error) {
            if (!this.currency || !this.currency.resolved) throw error;
            priceFormattingUnavailable = true;
          }
        } else {
          // Keep the server fallback if a stale dependency cannot safely format
          // presentment-currency amounts. Never hand them to the vendor parser.
          throw new Error('Price formatting is unavailable. Please reload the page.');
        }
        fragment.appendChild(card);
      });
      if (priceFormattingUnavailable) {
        // Keep card amounts and numeric facets in one currency if a legacy price
        // lacks exact metadata or conversion fails for a particular amount.
        this.currencyFailure = { key: this.currency.key, source: this.items, retryAt: Date.now() + 30000 };
        this.pricedCurrencyKey = null;
        if (this.loading) this.renderPartial(this.items);
        else this.render();
        return;
      }
      // Keep the current grid mounted during sizing/card construction. A temporary
      // empty grid can clamp the document's scroll position near the footer.
      if (replace) this.results.replaceChildren(fragment);
      else this.results.appendChild(fragment);
    }

    renderProgress() {
      var shown = Math.min(this.state.page * this.pageSize, this.matches.length);
      this.shown.textContent = this.matches.length > this.pageSize ? 'Showing ' + shown + ' of ' + this.matches.length : '';
      this.loadMoreButton.hidden = shown >= this.matches.length;
      if (this.mode === 'collection' && !this.loadMoreButton.hidden) {
        var nextPage = this.stateUrl();
        nextPage.searchParams.set('page', String(this.state.page + 1));
        if (this.dataset.defaultSort && this.state.sort === this.dataset.defaultSort) nextPage.searchParams.delete('sort_by');
        this.loadMoreButton.setAttribute('href', nextPage.href);
      }
      this.sentinel.hidden = shown >= this.matches.length;
    }

    showMore(fromButton) {
      if (this.loading || !this.matches || this.state.page * this.pageSize >= this.matches.length) return;
      var start = this.state.page * this.pageSize;
      this.state.page++;
      this.renderCards(start, this.state.page * this.pageSize);
      global.history.replaceState(null, '', this.stateUrl());
      this.renderProgress();
      if (fromButton && this.loadMoreButton.hidden) {
        var firstNew = this.results.children[start];
        var firstLink = firstNew && firstNew.querySelector('a:not([tabindex="-1"])');
        if (firstLink) firstLink.focus({ preventScroll: true });
      }
      // Re-observing allows a tall viewport to load another complete chunk when needed.
      if (this.observer && !this.sentinel.hidden) {
        this.observer.unobserve(this.sentinel);
        this.observer.observe(this.sentinel);
      }
    }

    disclosure(facet) {
      var details = this.el('details', 'tlt-filter-group');
      details.dataset.facetGroup = facet.key;
      var collapseKey = (this.media.matches ? 'mobile:' : 'desktop:') + facet.key;
      details.open = this.collapse.has(collapseKey) ? this.collapse.get(collapseKey)
        : !(this.media.matches ? facet.collapseMobile : facet.collapseDesktop);
      var summary = this.el('summary', 'tlt-filter-group-title', facet.label);
      summary.dataset.controlKey = 'summary:' + facet.key;
      details.appendChild(summary);
      details.addEventListener('toggle', () => this.collapse.set(collapseKey, details.open));
      return details;
    }

    renderFacets() {
      if (!this.items) return;
      var counts = this.engine.facetCounts(this.pricedItems || this.items, this.priceState || this.state, this.tree);
      var byKey = new Map(counts.map(function (facet) { return [facet.key, facet]; }));
      var fragment = global.document.createDocumentFragment();
      this.tree.options.forEach((option) => {
        if (option.type === 'price') {
          var price = this.renderPrice(option);
          if (price) fragment.appendChild(price);
          return;
        }
        var facet = byKey.get(option.key);
        if (!facet || facet.hidden) return;
        var details = this.disclosure(facet);
        var values = this.el('div', 'tlt-filter-values' + (facet.display === 'box' ? ' tlt-filter-values--boxes' : ''));
        values.setAttribute('role', 'group');
        values.setAttribute('aria-label', facet.label);
        facet.values.forEach((value) => {
          if (!value.count && !value.selected) return;
          var label = this.el('label', 'tlt-filter-option');
          var input = this.el('input');
          input.type = 'checkbox';
          input.value = value.value;
          input.checked = value.selected;
          input.dataset.facetKey = facet.key;
          input.dataset.controlKey = 'value:' + facet.key + ':' + value.value;
          input.name = 'tlt-' + facet.key;
          var caption = this.el('span', 'tlt-filter-option-caption');
          caption.appendChild(this.el('span', 'tlt-filter-option-label', value.label));
          caption.appendChild(this.el('span', 'tlt-filter-option-count', ' (' + value.count + ')'));
          label.appendChild(input);
          label.appendChild(caption);
          values.appendChild(label);
        });
        details.appendChild(values);
        fragment.appendChild(details);
      });
      this.facets.replaceChildren(fragment);
    }

    renderPrice(option) {
      var bounds = this.engine.priceBounds(this.pricedItems || this.items, this.priceState || this.state, this.tree);
      if (!bounds.count && this.state.min == null && this.state.max == null) return null;
      var details = this.disclosure(option);
      var holder = this.el('div', 'tlt-filter-price');
      var inputs = this.el('div', 'tlt-filter-price-inputs');
      var precision = this.currency ? this.currency.precision : 2;
      var factor = Math.pow(10, precision);
      var step = 1 / factor;
      var lower = bounds.min == null ? 0 : Math.floor(bounds.min / 100 * factor + 0.00000001) / factor;
      var upper = bounds.max == null ? Math.max(lower + step, (this.state.max || 100) / 100)
        : Math.ceil(bounds.max / 100 * factor - 0.00000001) / factor;
      upper = Math.max(lower + step, upper);
      var priceState = this.priceState || this.state;
      var values = { min: priceState.min == null ? lower : priceState.min / 100,
        max: priceState.max == null ? upper : priceState.max / 100 };
      ['min', 'max'].forEach((bound) => {
        var label = this.el('label');
        label.appendChild(this.el('span', '', (bound === 'min' ? 'From' : 'To') + ' (' + this.displayCurrency() + ')'));
        var number = this.el('input');
        number.type = 'number';
        number.min = '0';
        number.step = String(step);
        number.inputMode = 'decimal';
        number.dataset.priceBound = bound;
        number.dataset.controlKey = 'price-number:' + bound;
        number.value = priceState[bound] == null ? '' : String(priceState[bound] / 100);
        number.placeholder = String(bound === 'min' ? lower : upper);
        number.disabled = !!(this.currency && !this.currency.resolved);
        label.appendChild(number);
        inputs.appendChild(label);
      });
      holder.appendChild(inputs);
      var sliders = this.el('div', 'tlt-filter-price-sliders');
      var track = this.el('div', 'tlt-filter-price-track');
      var active = this.el('div', 'tlt-filter-price-track-active');
      var from = Math.max(0, Math.min(100, (values.min - lower) / (upper - lower) * 100));
      var to = Math.max(from, Math.min(100, (values.max - lower) / (upper - lower) * 100));
      active.style.left = from + '%';
      active.style.right = (100 - to) + '%';
      track.appendChild(active);
      sliders.appendChild(track);
      ['min', 'max'].forEach((bound) => {
        var range = this.el('input');
        range.type = 'range';
        range.min = String(lower);
        range.max = String(upper);
        range.step = String(step);
        range.value = String(values[bound]);
        range.disabled = !!(this.currency && !this.currency.resolved);
        range.dataset.priceBound = bound;
        range.dataset.controlKey = 'price-range:' + bound;
        range.setAttribute('aria-label', (bound === 'min' ? 'Minimum' : 'Maximum') + ' price (' + this.displayCurrency() + ')');
        range.setAttribute('aria-valuetext', this.money(Number(range.value) * 100));
        sliders.appendChild(range);
      });
      holder.appendChild(sliders);
      details.appendChild(holder);
      return details;
    }

    renderChips() {
      var count = activeFilterCount(this.state);
      this.chips.replaceChildren();
      this.chips.hidden = !count;
      var filterLabel = this.filterButton.querySelector('[data-filter-open-label]') || this.filterButton;
      filterLabel.textContent = 'Filter' + (count ? ' (' + count + ')' : '');
      if (!count) return;
      this.chips.appendChild(this.el('span', 'tlt-filter-refine-heading', 'Refine by'));
      var addChip = (key, value, label) => {
        var button = this.el('button', 'tlt-filter-chip', label + ' ×');
        button.type = 'button';
        button.dataset.removeKey = key;
        button.dataset.removeValue = value;
        button.dataset.controlKey = 'chip:' + key + ':' + value;
        button.setAttribute('aria-label', 'Remove ' + label + ' filter');
        this.chips.appendChild(button);
      };
      this.tree.options.forEach((facet) => {
        (this.state.selected[facet.key] || []).forEach((value) => {
          var option = (facet.values || []).find(function (entry) { return entry.value === value; });
          addChip(facet.key, value, option ? option.label : value);
        });
      });
      if (this.state.min != null || this.state.max != null) {
        var code = this.state.currency || this.displayCurrency();
        var label = this.state.min == null ? 'Up to ' + this.money(this.state.max, code)
          : this.state.max == null ? 'From ' + this.money(this.state.min, code)
            : this.money(this.state.min, code) + ' – ' + this.money(this.state.max, code);
        if (this.pricePaused) label += ' (paused)';
        addChip('price', '', label);
      }
      var clear = this.el('button', 'tlt-filter-clear', 'Clear all');
      clear.type = 'button';
      clear.dataset.filterClear = '';
      clear.dataset.controlKey = 'clear-all';
      this.chips.appendChild(clear);
    }

    updateCollectionImageSizes() {
      if (this.mode !== 'collection' || !this.results) return;
      var layout = collectionImageLayout(this.results, this.mobileColumns, this.media && this.media.matches);
      this.results.querySelectorAll('img[data-collection-responsive]').forEach(function (image) {
        if (image.sizes !== layout.sizes) image.sizes = layout.sizes;
      });
    }

    startCollectionImageWatch() {
      if (this.mode !== 'collection' || !this.results || this.imageObserver || this.onImageResize) return;
      this.onImageResize = () => this.updateCollectionImageSizes();
      if (global.ResizeObserver) {
        this.imageObserver = new global.ResizeObserver(this.onImageResize);
        this.imageObserver.observe(this.results);
      } else {
        global.addEventListener('resize', this.onImageResize);
      }
    }

    stopCollectionImageWatch() {
      if (this.imageObserver) this.imageObserver.disconnect();
      else if (this.onImageResize) global.removeEventListener('resize', this.onImageResize);
      this.imageObserver = null;
      this.onImageResize = null;
    }

    setMobileColumns(value) {
      if (this.mode !== 'collection' || ['1', '2'].indexOf(String(value)) === -1) return;
      this.mobileColumns = Number(value);
      this.style.setProperty('--tlt-mobile-columns', String(value));
      if (this.results) {
        this.results.classList.toggle('product-grid--per-row-mob-1', String(value) === '1');
        this.results.classList.toggle('product-grid--per-row-mob-2', String(value) === '2');
      }
      this.querySelectorAll('[data-filter-columns]').forEach(function (button) {
        button.setAttribute('aria-pressed', button.dataset.filterColumns === String(value) ? 'true' : 'false');
      });
      this.updateCollectionImageSizes();
    }

    syncCollectionLayout() {
      if (this.mode !== 'collection' || !this.media) return;
      var control = this.querySelector('[data-filter-sort-control]');
      var slot = this.querySelector(this.media.matches ? '[data-filter-sort-mobile]' : '[data-filter-sort-desktop]');
      if (control && slot && control.parentElement !== slot) slot.appendChild(control);
      this.setMobileColumns(this.mobileColumns || (this.dataset.mobileColumns === '1' ? 1 : 2));
    }

    syncDrawer() {
      if (!this.sidebar || !this.media) return;
      var closed = this.media.matches && !this.drawerOpen;
      this.sidebar.inert = closed;
      if (closed) this.sidebar.setAttribute('aria-hidden', 'true');
      else this.sidebar.removeAttribute('aria-hidden');
      if (this.media.matches && this.drawerOpen) {
        this.sidebar.setAttribute('role', 'dialog');
        this.sidebar.setAttribute('aria-modal', 'true');
      } else {
        this.sidebar.removeAttribute('role');
        this.sidebar.removeAttribute('aria-modal');
      }
      this.classList.toggle('tlt-drawer-open', !!this.drawerOpen);
      this.filterButton.setAttribute('aria-expanded', this.drawerOpen ? 'true' : 'false');
      this.backdrop.hidden = !this.drawerOpen;
    }

    openDrawer() {
      if (!this.media.matches || this.drawerOpen) return;
      this.previousFocus = global.document.activeElement;
      this.previousOverflow = global.document.body.style.overflow;
      global.document.body.style.overflow = 'hidden';
      this.drawerOpen = true;
      this.syncDrawer();
      this.sidebar.querySelector('[data-filter-close]').focus();
    }

    closeDrawer(restoreFocus) {
      if (!this.drawerOpen) return;
      this.drawerOpen = false;
      global.document.body.style.overflow = this.previousOverflow || '';
      this.syncDrawer();
      if (restoreFocus && this.previousFocus && this.previousFocus.isConnected) {
        this.previousFocus.focus({ preventScroll: !!this.revealResultsOnRender });
      }
      this.revealUpdatedResults();
    }

    handleKey(event) {
      if (!this.drawerOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDrawer(true);
        return;
      }
      if (event.key !== 'Tab') return;
      var focusable = Array.from(this.sidebar.querySelectorAll('button, input, select, summary, a[href], [tabindex="0"]'))
        .filter(function (element) { return !element.disabled && element.getClientRects().length; });
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (!first) { event.preventDefault(); this.sidebar.focus(); return; }
      if (event.shiftKey && (global.document.activeElement === first || global.document.activeElement === this.sidebar)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (global.document.activeElement === last || global.document.activeElement === this.sidebar)) {
        event.preventDefault(); first.focus();
      }
    }
  }

  global.customElements.define('tlt-filtered-results', FilteredResults);
})(typeof window !== 'undefined' ? window : globalThis);
